import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from "../../../Components/UI/dialog";
import { Button } from "../../../Components/UI/button";
import { Label } from "../../../Components/UI/label";
import { Check, X, Loader2, ArrowLeft } from "lucide-react";
import CustomSearchableDropdown from "../../../Components/Common/CustomSearchableDD";
import { DatePicker } from "../../../Components/Common/DatePicker";
import { format } from "date-fns";
import { setGlobalLoading } from "../../../Store/Actions";
import { toast } from "sonner";
import { FaFacebook } from "react-icons/fa";
import { AlertBox } from "../../../Components/Common/AlertBox";
import apiService from "../../../Services/APIService";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../Components/UI/table";
import { Card, CardContent } from "../../../Components/UI/card";
import MastersDialog from "../../Settings/Masters";

const MetaLeadImport = ({ open, onOpenChange }) => {
  const [loading, setLoading] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const dispatch = useDispatch();

  const [pages, setPages] = useState([]);
  const [forms, setForms] = useState([]);

  const [selectedPage, setSelectedPage] = useState("");
  const [selectedForm, setSelectedForm] = useState("");
  const [selectedSource, setSelectedSource] = useState("");
  const [fromDT, setFromDT] = useState("");

  const longLivedToken = useSelector((state) => state.masterData.fbLongLivedToken)
  const fbLongLivedToken = (longLivedToken && longLivedToken.length > 0) && longLivedToken[0];
  const userID = useSelector((state) => state.masterData.fbUserID);
  const fbUserID = (userID && userID.length > 0) && userID[0];


  // Get sources from Redux
  const sources = useSelector((state) => state.masterData.sources || []);
  const sourceOptions = sources.find(s => s.TID === 1)?.Srcs || [];
  const agencyData = useSelector((state) => state.agencyData.data);

  const [activeDialog, setActiveDialog] = useState(null);
  const [errorDialogData, setErrorDialogData] = useState([]);

  const [sourceDialogOpen, setSourceDialogOpen] = useState(false);

  useEffect(() => {
    window.fbAsyncInit = function () {
      const appId = process.env.REACT_APP_FB_APP_ID;

      if (!appId) {
        console.error('Facebook App ID not found. Check your .env file.');
        return;
      }
      window.FB.init({
        appId: appId,
        cookie: true,
        xfbml: true,
        version: 'v18.0'
      });

      window.FB.AppEvents.logPageView();
    };

    (function (d, s, id) {
      var js, fjs = d.getElementsByTagName(s)[0];
      if (d.getElementById(id)) { return; }
      js = d.createElement(s); js.id = id;
      js.src = "https://connect.facebook.net/en_US/sdk.js";
      fjs.parentNode.insertBefore(js, fjs);
    }(document, 'script', 'facebook-jssdk'));
  }, [])

  useEffect(() => {
    if (open && fbLongLivedToken) {
      setLoading(true);
      fetchFBPages(fbUserID, fbLongLivedToken); // Use null for userID as long-lived token should handle it
      setLoading(false);
    }
  }, [open, fbLongLivedToken]);

  // Function which will handle meta login
  const handleLoginWithFacebook = () => {
    setLoading(true);

    if (typeof window.FB === 'undefined') {
      toast.error('Facebook SDK not loaded. Please refresh the page.');
      setLoading(false);
      return;
    }

    window.FB.login((response) => {
      if (response.authResponse) {
        const token = response.authResponse.accessToken;
        const userID = response.authResponse.userID;
        // Fetch long-lived token and save to database
        fetchAndSaveLongLivedToken(token, userID);
      } else {
        console.log('User cancelled login or did not fully authorize.');
        toast.error('Facebook login was cancelled or denied.');
      }
      setLoading(false);
    },
      {
        scope: 'email,pages_show_list,leads_retrieval,pages_read_engagement,pages_manage_metadata,pages_manage_ads,business_management,read_insights',
        return_scopes: true
      });
  };

  // Function which will handle meta logout
  const handleLogout = async () => {
    setLoading(true);

    if (typeof window.FB === 'undefined') {
      handleReset();
      setLoading(false);
      return;
    }
    const result = await apiService.sendChange(1, 'setFBAccess', {
      userAccessToken: fbLongLivedToken,
      changeType: 'FBLOGOUT',
      fbUserID: fbUserID
    });
    setLoading(false);

    window.FB.logout((response) => {
      console.log('User logged out from Facebook.');
      handleReset();
      // TODO: BACKEND API CALL to delete long-lived token from database
      setLoading(false);
    });
  };

  // Function which will set user's fb access
  const fetchAndSaveLongLivedToken = async (token, userID) => {
    try {
      const result = await apiService.sendChange(1, 'setFBAccess', {
        userAccessToken: token,
        changeType: 'FBTKN',
        fbUserID: userID
      });
 
      // Success is silent - token is stored in backend
    } catch (error) {
      console.error("Error seting long-lived token:", error);
    }
  };

  // Fetch FB pages from backend (which fetches from Meta and saves to DB)
  const fetchFBPages = async (userID, token) => {
    dispatch(setGlobalLoading(true));
    try {
      const result = await apiService.getData(1, 'getFBPages', {
        fbUserID: userID,
        fbUserToken: token
      });

      const dataSet = JSON.parse(result.updatePackage);

      if (dataSet.Table[0].ErrCode === "-1" && dataSet.Table1) {
        // Parse pages from backend response
        const pagesData = dataSet.Table1.map(page => ({
          id: page.FBPageID,
          name: page.PageName,
          accessToken: page.PageToken
        }));
        setPages(pagesData);
        // toast.success("Facebook pages loaded.");
      } else {
        // toast.error(dataSet.Table[0].ErrMsg || "No facebook pages found.");
        setPages([]);
      }
    } catch (error) {
      console.error("Error fetching pages:", error);
      toast.error("Failed to fetch facebook pages. Please try again.");
      setPages([]);
    } finally {
      dispatch(setGlobalLoading(false));
    }
  };

  // Fetch FB forms from backend (which fetches from Meta and saves to DB)
  const fetchFBForms = async (pageID, token) => {
    if (!pageID) return;

    dispatch(setGlobalLoading(true));
    try {
      const result = await apiService.getData(1, 'getFBForms', {
        fbPageID: pageID,
        fbPageToken: token
      });

      const dataSet = JSON.parse(result.updatePackage);

      if (dataSet.Table[0].ErrCode === "-1" && dataSet.Table1) {
        // Parse forms from backend response
        const formsData = dataSet.Table1.map(form => ({
          id: form.FBFormID,
          name: form.FormName
        }));
        setForms(formsData);

        if (formsData.length === 0) {
          toast.error("No lead forms found for this page.");
        }
      } else {
        toast.error(dataSet.Table[0].ErrMsg || "No lead forms found");
        setForms([]);
      }
    } catch (error) {
      console.error("Error fetching forms:", error);
      toast.error("Failed to fetch lead forms. Please try again.");
      setForms([]);
    } finally {
      dispatch(setGlobalLoading(false));
    }
  };

  // Function which will handle fb page change
  const handlePageChange = (pageID) => {
    setSelectedPage(pageID);
    setSelectedForm("");
    setSelectedSource("");
    setFromDT("");
    setForms([]);

    if (pageID) {
      // Find the selected page and use its access token
      const selectedPageData = pages.find(p => p.id === pageID);
      if (selectedPageData) {
        // Fetch forms for selected page
        fetchFBForms(pageID, selectedPageData.accessToken);
      }
    }
  };

  // Function whcih will handle fb form change
  const handleFormChange = (formID) => {
    setSelectedForm(formID);
    if (!formID) {
      setSelectedSource("");
      setFromDT("");
    }
  };

  // Function which will handle import inquiries
  const fetchFBLeads = async () => {
    dispatch(setGlobalLoading(true));
    try {
      const selectedPageData = pages.find(p => p.id === selectedPage);
      const result = await apiService.sendChange(1, 'setFBLeads', {
        fbFormID: selectedForm,
        fbPageToken: selectedPageData.accessToken,
        source: selectedSource,
        fromDT: fromDT,
        pocUserID: agencyData.POCUserID,
        changeType: "META",
        typeID: 1,
      });

      const dataSet = JSON.parse(result.updatePackage);
      const dataSetTbl = dataSet?.Table?.[0];
      let errors = [];

      if (dataSetTbl?.FnErrCode === "-1") {
        const retData = JSON.parse(dataSetTbl.RetData);
        const leads = retData?.ThreadsData || [];
        errors = leads.filter(l => l.IsError);
      } else if (dataSetTbl?.ErrCode) {
        // Case when no leads or other error
        errors = [{ ErrMsg: dataSetTbl.ErrMsg }];
      } else {
        console.error("Unexpected response format.", dataSetTbl);
      }

      if (errors.length > 0) {
        setErrorDialogData(errors);
        setActiveDialog("error");
      } else {
        setShowSuccessAlert(true);
      }

    } catch (err) {
      console.error("Error fetching leads", err);
    } finally {
      dispatch(setGlobalLoading(false));
    }
  };

  const handleSuccessAlertClose = () => {
    setShowSuccessAlert(false);
    onOpenChange(false);
    handleReset();
  };

  const handleReset = () => {
    setPages([]);
    setForms([]);
    setSelectedPage("");
    setSelectedForm("");
    setSelectedSource("");
    setFromDT("");
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-[100vw] max-h-[100vh] w-full h-full p-0 m-0">
          <DialogHeader>
            {/* Mobile View */}
            <div className="sm:hidden flex items-center justify-between w-full h-8">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <DialogClose className="p-0 w-auto h-auto flex-shrink-0 flex justify-center">
                  <ArrowLeft className="h-6 w-6" />
                  <span className="sr-only">Back</span>
                </DialogClose>
                <DialogTitle className="text-lg font-medium">
                  Meta Lead Import
                </DialogTitle>
              </div>
            </div>

            {/* Desktop Title */}
            <DialogTitle className="hidden sm:block text-lg md:text-xl">
              Meta Lead Import
            </DialogTitle>

            {/* Desktop Right Side */}
            <div className="hidden sm:flex items-center gap-2">
              <DialogClose className="flex-shrink-0 p-1 rounded-md transition-colors">
                <X className="h-5 w-5" />
                <span className="sr-only">Close</span>
              </DialogClose>
            </div>
          </DialogHeader>

          <div className="px-3 sm:px-4 space-y-4">
            {!fbLongLivedToken ? (
            <div className="text-center py-6 sm:py-8 space-y-4">
              <div className="text-gray-600 text-sm sm:text-base px-2">
                Please log in with Facebook to import leads from your Meta pages.
              </div>
              <Button
                className="mx-auto flex items-center gap-2 w-full sm:w-auto"
                onClick={handleLoginWithFacebook}
                disabled={loading}
              >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : <FaFacebook />}
                  {loading ? "Connecting..." : "Log in with Facebook"}
                </Button>
              </div>
            ) : (
              <>
                {/* Select Page */}
                <div className="space-y-1.5">
                  <Label required={true} className="text-sm">
                    Meta Page
                  </Label>
                  <CustomSearchableDropdown
                    options={[
                      ...pages.map((p) => ({
                        value: p.id,
                        label: p.name,
                      })),
                    ]}
                    value={selectedPage}
                    onSelect={handlePageChange}
                    placeholder="Select Facebook Page"
                    fieldID="pageSelect"
                    searchEnabled={true}
                    allowAdd={false}
                    className="h-11"
                  />
                </div>

                {/* Select Form */}
                <div className="space-y-1.5">
                  <Label required={true} className="text-sm">
                    Meta Form
                  </Label>
                  <CustomSearchableDropdown
                    options={[
                      ...forms.map((f) => ({
                        value: f.id,
                        label: f.name,
                      }))
                    ]}
                    value={selectedForm}
                    onSelect={handleFormChange}
                    placeholder="Select Lead Form"
                    fieldID="formSelect"
                    searchEnabled={true}
                    allowAdd={false}
                    className="h-11"
                    disabled={!selectedPage}
                  />
                </div>

                {/* Select Source and From Date */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Select Source */}
                  <div className="space-y-1.5">
                    <Label required={true} className="text-sm">
                      Source to consider
                    </Label>
                    <CustomSearchableDropdown
                      options={[
                        ...sourceOptions.map((s) => ({
                          value: s.Src,
                          label: s.Src,
                        })),
                      ]}
                      value={selectedSource}
                      onSelect={(val) => {
                        if (val === 'add') {
                          setSourceDialogOpen(true);
                          return;
                        }
                        setSelectedSource(val)
                      }}
                      placeholder="Select Source"
                      fieldID="sourceSelect"
                      searchEnabled={true}
                      allowAdd={true}
                      addText={`Add new source`}
                      className="h-11"
                      disabled={!selectedForm}
                    />
                  </div>

                  {/* From Date */}
                  <div className="space-y-1.5">
                    <Label required={true} className="text-sm">
                      Import from
                    </Label>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                      <div className="w-full sm:w-[70%]">
                        <DatePicker
                          fieldID="fromDT"
                          placeholder="Select Date"
                          required={true}
                          value={fromDT ? new Date(fromDT) : undefined}
                          onChange={(date) => setFromDT(date ? format(date, "yyyy-MM-dd") : "")}
                          disabled={!selectedForm}
                          maxDate={new Date()}
                        />
                      </div>
                      <span className="italic text-gray-500 text-xs sm:text-sm">till today</span>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Footer with buttons */}
          {fbLongLivedToken && (
            <div className="flex flex-col sm:flex-row justify-start items-stretch sm:items-center gap-2 px-3 sm:px-4 py-3">
              <Button
                className="w-full sm:w-auto order-1"
                onClick={fetchFBLeads}
                disabled={loading || !selectedPage || !selectedForm || !selectedSource || !fromDT}
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Check className="w-4 h-4" />
                )}
                Import Inquiries
              </Button>

              <Button
                className="w-full sm:w-auto bg-sahi-red order-2"
                onClick={handleLogout}
                disabled={loading}
              >
                <FaFacebook />
                Log Out
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Success Alert Box */}
      <AlertBox
        open={showSuccessAlert}
        onOpenChange={setShowSuccessAlert}
        title="Success"
        description="Inquiries imported from Meta."
        actions={[
          {
            label: "OK",
            value: "ok",
            variant: "default",
            icon: <Check className="w-4 h-4" />,
          }
        ]}
        onAction={handleSuccessAlertClose}
      />

      {activeDialog === "error" && (
        <Dialog
          open={activeDialog === "error"}
          onOpenChange={(e) => {
            if (!e) setActiveDialog("");
          }}
        >
          <DialogContent className="w-[95vw] sm:w-full max-w-6xl max-h-[90vh] pb-4 sahi-radius overflow-hidden">

            <DialogHeader className="dialog-header flex items-center justify-between px-4 pt-4">
              <div className="flex items-center gap-2">
                <DialogTitle className="font-semibold text-lg">
                  {`Import ${errorDialogData.length === 1 ? "Error" : "Errors"}`}
                </DialogTitle>
              </div>

              <DialogClose className="p-1 sahi-radius">
                <X className="h-5 w-5" />
              </DialogClose>
            </DialogHeader>

            <div className="px-4 ">
              <Card className="plain" >
                <CardContent className="p-0">
                  <div className="max-h-[50vh] sm:max-h-[calc(100vh-110px)] lg:max-h-[60vh] overflow-auto">
                    <Table className="min-w-full table-auto">
                      <TableHeader >
                        <TableRow>
                          <TableHead className="w-[40px] text-xs sm:text-sm">No.</TableHead>
                          <TableHead className="text-xs sm:text-sm">Contact</TableHead>
                          <TableHead className="text-xs sm:text-sm">Errors</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {errorDialogData.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={3} className="text-center py-8">
                              Empty data.
                            </TableCell>
                          </TableRow>
                        ) : (
                          errorDialogData.map((entry, index) => (
                            <TableRow key={index} className="align-top">
                              <TableCell className="w-[40px] px-2 sm:px-4 align-top text-xs sm:text-sm">{index + 1}</TableCell>
                              <TableCell className="align-top px-2 sm:px-4 text-xs sm:text-sm">{entry.BizName || "-"}</TableCell>
                              <TableCell className="!text-sahi-red align-top px-2 sm:px-4 text-xs sm:text-sm">
                                {[...new Set((entry.ErrMsg || "Unknown error")
                                  .split(".")
                                  .map(s => s.trim())
                                  .filter(Boolean)
                                )].map((msg, i) => (
                                  <div key={i}>• {msg}.</div>
                                ))}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="w-full sm:w-fit !sahi-radius px-3 sm:px-4">
              <Button
                variant="secondary"
                onClick={() => setActiveDialog("")}
                className="w-full sm:w-fit cls-btn"
              >
                <X className="h-4 w-4" />
                Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {sourceDialogOpen && <MastersDialog
        open={sourceDialogOpen}
        onClose={() => {
          setSourceDialogOpen(false)
        }}
        directAdd={true}
        openEntry={true}
        masterType='sources'
        onProductAdded={(itemName) => {
          setSelectedSource(itemName)
          setSourceDialogOpen(false)
        }}
      />}
    </>
  );
};

export default MetaLeadImport;